import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Grid, Container, CircularProgress} from '@mui/material';
import EventGallery from '../components/events/EventGallery';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import { useCart } from '../contexts/CartContext';
import CartContext from '../contexts/CartContext';
import addToCartItem from '../hooks/cart/addToCartItem';
import addToWaitlist from '../hooks/cart/addToWaitlist';
import removeItemFromCart from '../hooks/cart/removeItemFromCart';
import { getClientId, getEventStatus } from '../utils';
import axios from 'axios';
import ActionConfirmation from '../components/elements/ActionConfirmation';
import ConfirmDialog from '../components/elements/ConfirmDialog';
import type { ActionConfirmationVariant } from '../types';
import { EventDetail, CartItemType, ExpandedAccordionsType } from '../types';
import EventKeyInformation from '../components/events/EventKeyInformation';
import EventPricingTiersBoxes from '../components/events/EventPricingTiersBoxes';
import EventStatusPaper from '../components/events/EventStatusPaper';
import EventDescription from '../components/events/EventDescription';
import EventHeader from '../components/events/EventHeader';
import { KEY_EVENT_STATUS_ACTIVE } from '../constants';
import { useScrollToConfirmation } from '../hooks/useScrollToConfirmation';
import { getReturnToForEvents } from '../utils/navigationReturnTo';

const EventPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [event, setEvent] = useState<EventDetail | null>(null);
    const [eventDetails, setEventDetails] = useState<EventDetail | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const { user, selectedChild } = useUser();
    // Always resolve the selected child from user.children[] by ID so any DOB update is immediately
    // reflected (avoids using a detached copy stored in user.selectedChild or the selectedChild state).
    const resolvedSelectedChild = (() => {
        if (!user?.children?.length) return null;
        const targetId = (user.selectedChild ?? selectedChild)?.id;
        return (targetId ? user.children.find((c) => c.id === targetId) : undefined)
            ?? user.children[0];
    })();
    const effectiveUser = user?.children?.length
        ? { ...user, selectedChild: resolvedSelectedChild }
        : user;
    const { incrementCartItemsTotal, fetchCart } = useCart();
    const { cartItems } = useContext(CartContext) as unknown as { cartItems: CartItemType };
    const events_api_url = import.meta.env.VITE_EVENTS_API_URL;
    const client_id = getClientId();
    const [currentPricingId, setCurrentPricingId] = useState<number | null>(null);
    const [currentSelectedDays, setCurrentSelectedDays] = useState<string[] | null>(null);
    const [isPricingUpdating, setIsPricingUpdating] = useState<boolean>(false);
    const { handleLoginClick } = useAuth();
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [openWaitlistDialog, setOpenWaitlistDialog] = useState<{ id: number | null; open: boolean }>({ id: null, open: false });
    const [confirmation, setConfirmation] = useState<{ visible: boolean; variant: ActionConfirmationVariant; message: string }>({ visible: false, variant: 'success', message: '' });
    const [expandedAccordions, setExpandedAccordions] = useState<ExpandedAccordionsType>({});

    useScrollToConfirmation(confirmation.visible, confirmation.message, 'event-page-confirmation');

    // Make browser Back behave like the in-page Back button:
    // when the user presses the browser's back button while on the Event page,
    // navigate to the same stored "return to" path used by BackButton.
    useEffect(() => {
        const handlePopState = (_event: PopStateEvent) => {
            navigate(getReturnToForEvents(), { replace: true });
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [navigate]);

    useEffect(() => {
        const fetchEventDetails = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${events_api_url}/events/${id}`, {
                    params: { client_id, isPublic: 1 }
                });
                setEvent(response.data.data);
                setEventDetails(response.data.data);
            } catch (error) {
                console.error('Error fetching event details:', error);
                setConfirmation({ visible: true, variant: 'error', message: 'Failed to fetch event details.' });
            } finally {
                setLoading(false);
            }
        };

        fetchEventDetails();
    }, [id]);

    const handleShare = () => {
        navigator.clipboard.writeText(window.location.href);
        // Message is now shown next to the icon, not in snackbar
    };

    const handleAddToCartClick = async (pricingId: number, isSelectScheduleAllowed: boolean, maxScheduleDays: number, selectedDaysArray: string[], handleAccordion: boolean) => {
        if (!effectiveUser) {
            handleLoginClick();
            return;
        }

        try {
            if (isSelectScheduleAllowed) {
                if (handleAccordion) {
                    setExpandedAccordions((prev) => ({
                        ...prev,
                        [pricingId]: !prev[pricingId],
                    }));
                } else if (selectedDaysArray && selectedDaysArray.length <= maxScheduleDays) {
                    if (selectedDaysArray.length < maxScheduleDays) {
                        handleOpenDialog(pricingId, selectedDaysArray);
                    } else {
                        setIsPricingUpdating(true);
                        const arrayResponse = await addToCartItem(pricingId, effectiveUser.selectedChild!.id, selectedDaysArray);
                        setConfirmation({ visible: true, variant: arrayResponse.success ? 'success' : 'error', message: arrayResponse.message });
                        if (arrayResponse.success) {
                            incrementCartItemsTotal();
                            await fetchCart();
                            // Close the schedule selector so the outer Add to Cart button is visible.
                            setExpandedAccordions((prev) => ({
                                ...prev,
                                [pricingId]: false,
                            }));
                        }
                    }
                } else {
                    setConfirmation({ visible: true, variant: 'error', message: `You can only select up to ${maxScheduleDays} days.` });
                }
            } else {
                setIsPricingUpdating(true);
                const arrayResponse = await addToCartItem(pricingId, effectiveUser.selectedChild!.id);
                setConfirmation({ visible: true, variant: arrayResponse.success ? 'success' : 'error', message: arrayResponse.message });
                if (arrayResponse.success) {
                    incrementCartItemsTotal();
                    await fetchCart();
                    setExpandedAccordions((prev) => ({
                        ...prev,
                        [pricingId]: false,
                    }));
                }
            }
        } finally {
            setIsPricingUpdating(false);
        }
    };

    const handleAddToWaitlist = (pricingId: number) => {
        setOpenWaitlistDialog({ id: pricingId, open: true });
    };

    const handleCloseWaitlistDialog = () => {
        setOpenWaitlistDialog({ id: null, open: false });
    };

    const handleConfirmWaitlist = async () => {
        if (!effectiveUser || !effectiveUser.selectedChild || openWaitlistDialog.id === null) return;

        const response = await addToWaitlist({
            pricingId: openWaitlistDialog.id,
            childId: effectiveUser.selectedChild.id,
            userId: Number(effectiveUser.uid),
        });

        setConfirmation({ visible: true, variant: response.success ? 'success' : 'error', message: response.message });
        handleCloseWaitlistDialog();
    };

    const handleOpenDialog = (pricingId: number, selectedDays: string[]) => {
        setCurrentPricingId(pricingId);
        setCurrentSelectedDays(selectedDays);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleConfirmAddToCart = async () => {
        if (!currentPricingId || !effectiveUser || !effectiveUser.selectedChild || !currentSelectedDays) return;

        setIsPricingUpdating(true);
        try {
            const arrayResponse = await addToCartItem(currentPricingId, effectiveUser.selectedChild.id, currentSelectedDays);
            setConfirmation({ visible: true, variant: arrayResponse.success ? 'success' : 'error', message: arrayResponse.message });
            if (arrayResponse.success) {
                incrementCartItemsTotal();
                await fetchCart();
                setExpandedAccordions((prev) => ({
                    ...prev,
                    [currentPricingId]: false,
                }));
            }
        } finally {
            setIsPricingUpdating(false);
            handleCloseDialog();
        }
    };

    const handleDismissConfirmation = () => {
        setConfirmation((c) => ({ ...c, visible: false }));
    };

    const isItemInCart = (pricingId: number, childId: number): boolean => {
        if (cartItems && cartItems.data && cartItems.data.length > 0) {
            return cartItems.data.some(item => item.item.id === pricingId && item.child.id === childId);
        }
        return false;
    };

    const isEventInCart = (eventId: number, childId: number): boolean => {
        return cartItems?.data?.some(item => 
            item.item.event.id === eventId && item.child.id === childId
        ) || false;
    };

    const handleRemoveItemFromCart = async (pricingId: number) => {
        setIsPricingUpdating(true);
        try {
            if (!effectiveUser || !effectiveUser.selectedChild) return;

            const cartItem = cartItems?.data?.find(item => item.item.id === pricingId && item.child.id === effectiveUser.selectedChild!.id);
            
            if (!cartItem) {
                setConfirmation({ visible: true, variant: 'error', message: 'Item not found in cart.' });
                return;
            }
    
            const success = await removeItemFromCart(cartItem.id);
            if (success) {
                await fetchCart();
                setConfirmation({ visible: true, variant: 'success', message: 'Item removed from cart.' });
            } else {
                setConfirmation({ visible: true, variant: 'error', message: 'Failed to remove item from cart.' });
            }
        } catch (error) {
            setConfirmation({ visible: true, variant: 'error', message: 'Failed to remove item from cart.' });
        } finally {
            setIsPricingUpdating(false);
        }
    };

    if (loading) {
        return <Container maxWidth="lg">
            <Grid container justifyContent="center" alignItems="center" textAlign="center" spacing={2}>
                <Grid item xs={12} sm={12} mt={5}>
                    <CircularProgress />
                </Grid>
            </Grid>    
        </Container>;
    }

    if (!event || event.status != KEY_EVENT_STATUS_ACTIVE || !eventDetails || event.isVisible == 0) {
        return <Container maxWidth="lg">
            <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                <Grid item xs={12} sm={9} mt={5}>
                    <Typography variant="h5">We’re unable to display this event right now.</Typography>
                    { (!event || !eventDetails) && <Typography variant="body1">The event you are looking for does not exist or has no pricings available.</Typography> }
                    { event && event.status != KEY_EVENT_STATUS_ACTIVE && <Typography variant="body1">The event you are looking for is not active.</Typography> }
                    { event && event.isVisible == 0 && <Typography variant="body1">The event you are looking for is not visible.</Typography> }
                </Grid>
            </Grid>    
        </Container>;
    }

    const { className } = getEventStatus(event, effectiveUser?.selectedChild?.id);

    return (
        <Container maxWidth="lg" className='event-page' sx={{ pb: 4, minHeight: 'auto' }}>
            <ActionConfirmation
                id="event-page-confirmation"
                visible={confirmation.visible}
                variant={confirmation.variant}
                message={confirmation.message}
                onDismiss={handleDismissConfirmation}
            />
            <EventHeader eventName={event.name} eventLocation={event.location?.name || ''} onShare={handleShare} />

            <Grid container spacing={4}>
                <Grid item xs={12} sm={12}>
                    <EventGallery images={event.images || []} />
                </Grid>
            </Grid>

            <Grid container spacing={4}>
                <Grid item xs={12} md={9}>
                    <Grid container spacing={4}>
                        <Grid item xs={12}>
                            <EventDescription event={event} />
                            <hr />
                        </Grid>
                        <Grid item xs={12} className='ptf-0'>
                            <EventKeyInformation event={event} />
                            <hr />
                        </Grid>
                        <Grid item xs={12} className='ptf-0'>
                            <EventPricingTiersBoxes
                                eventDetails={event}
                                user={effectiveUser}
                                className={className}
                                isItemInCart={isItemInCart}
                                isEventInCart={isEventInCart}
                                handleAddToCartClick={handleAddToCartClick}
                                handleRemoveItemFromCart={handleRemoveItemFromCart}
                                handleAddToWaitlist={handleAddToWaitlist}
                                expandedAccordions={expandedAccordions}
                                setExpandedAccordions={setExpandedAccordions}
                                isPricingUpdating={isPricingUpdating}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Grid container spacing={4}>
                        <Grid item xs={12}>
                            <EventStatusPaper event={event} selectedChildId={effectiveUser?.selectedChild?.id} />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <ConfirmDialog
                open={openDialog}
                handleClose={handleCloseDialog}
                handleConfirm={handleConfirmAddToCart}
                title="Confirm Action"
                description="You have selected fewer days than the maximum allowed. Are you sure you want to proceed?"
            />
            <ConfirmDialog
                open={openWaitlistDialog.open}
                handleClose={handleCloseWaitlistDialog}
                handleConfirm={handleConfirmWaitlist}
                title={`${event.name} waitlist`}
                description={`Please confirm you would like to be added to the ${event.name} waitlist`}
            />
        </Container>
    );
};

export default EventPage;